import { CloseOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Card, message, Select, Space, Modal} from 'antd';
import React, { useEffect, useState } from 'react';
import { slugify } from 'utils/slugify';
import { globalSettingService } from '../services/globalSettingService';
import { CountryInterface, RegionInterface } from '../types/globalSettingType';

interface RegionsSectionProps {
  countries: CountryInterface[];
  onCountriesChange: () => void;
}

const RegionsSection: React.FC<RegionsSectionProps> = ({ countries }) => {
  const [regions, setRegions] = useState<RegionInterface[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState<RegionInterface | null>(null);
  const [newGroupName, setNewGroupName] = useState('');
  const [selectedCountryIds, setSelectedCountryIds] = useState<number[]>([]);

  useEffect(() => {
    fetchRegions();
  }, []);

  const fetchRegions = async () => {
    try {
      setLoading(true);
      const response = await globalSettingService.fetchRegions();
      setRegions(response.results);
    } catch (error) {
      console.error('Failed to fetch regions:', error);
      Modal.error({ title: 'Error', content: 'Failed to load regions', okText: 'Confirm' });
    } finally {
      setLoading(false);
    }
  };

  const handleAddGroup = async () => {
    if (!newGroupName.trim()) {
      Modal.error({ title: 'Error', content: 'Please enter a group name', okText: 'Confirm' });
      return;
    }

    try {
      setLoading(true);
      const slug = slugify(newGroupName);
      await globalSettingService.createRegion({
        name: newGroupName.trim(),
        slug,
        is_custom: true,
        country_ids: [],
      });
      message.success('Region group created successfully');
      setNewGroupName('');
      await fetchRegions();
    } catch (error: any) {
      console.error('Failed to create region:', error);
      Modal.error({ title: 'Error', content: error?.response?.data?.detail || 'Failed to create region group', okText: 'Confirm' });
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveGroup = async (regionId: number) => {
    try {
      setLoading(true);
      // Note: Use DELETE API if available, otherwise handle with empty update
      // await globalSettingService.deleteRegion(regionId);
      await globalSettingService.updateRegion(regionId, { name: '', slug: '' });
      message.success('Region group removed');
      await fetchRegions();
      if (selectedRegion?.id === regionId) {
        setSelectedRegion(null);
      }
    } catch (error: any) {
      console.error('Failed to remove region:', error);
      Modal.error({ title: 'Error', content: error?.response?.data?.detail || 'Failed to remove region group', okText: 'Confirm' });
    } finally {
      setLoading(false);
    }
  };

  const handleAddCountry = async () => {
    if (!selectedRegion || selectedCountryIds.length === 0) {
      Modal.error({ title: 'Error', content: 'Please select a region and countries', okText: 'Confirm' });
      return;
    }

    try {
      setLoading(true);
      await globalSettingService.addCountriesToRegion(selectedRegion.id, selectedCountryIds);
      message.success('Countries added to region');
      setSelectedCountryIds([]);
      await fetchRegions();
      if (selectedRegion) {
        const updated = await globalSettingService.fetchRegionById(selectedRegion.id);
        setSelectedRegion(updated);
      }
    } catch (error: any) {
      console.error('Failed to add countries:', error);
      Modal.error({ title: 'Error', content: error?.response?.data?.detail || 'Failed to add countries', okText: 'Confirm' });
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveCountry = async (countryId: number) => {
    if (!selectedRegion) return;

    try {
      setLoading(true);
      await globalSettingService.removeCountriesFromRegion(selectedRegion.id, [countryId]);
      message.success('Country removed from region');
      await fetchRegions();
      if (selectedRegion) {
        const updated = await globalSettingService.fetchRegionById(selectedRegion.id);
        setSelectedRegion(updated);
      }
    } catch (error: any) {
      console.error('Failed to remove country:', error);
      Modal.error({ title: 'Error', content: error?.response?.data?.detail || 'Failed to remove country', okText: 'Confirm' });
    } finally {
      setLoading(false);
    }
  };

  const handleSelectRegion = async (region: RegionInterface) => {
    try {
      const fullRegion = await globalSettingService.fetchRegionById(region.id);
      setSelectedRegion(fullRegion);
    } catch (error) {
      console.error('Failed to fetch region details:', error);
      setSelectedRegion(region);
    }
  };

  return (
    <div style={{ marginBottom: '32px' }}>
      <Card
        title="Regions"
        extra={
          <div style={{ fontSize: '12px', color: '#999', fontStyle: 'italic' }}>
            Used for serviceable region block. 1) Create custom group 2) Add/Remove countries
          </div>
        }
        style={{ border: '2px solid #d9d9d9' }}
        headStyle={{ borderBottom: '2px solid #d9d9d9' }}
      >
        <div style={{ display: 'flex', gap: '24px' }}>
          {/* Left: Region Groups */}
          <div style={{ flex: 1 }}>
            <div style={{ marginBottom: '16px', fontWeight: 'bold' }}>Region Groups</div>
            <div
              style={{
                border: '1px solid #d9d9d9',
                borderRadius: '4px',
                padding: '12px',
                minHeight: '200px',
              }}
            >
              {regions.map(region => (
                <div
                  key={region.id}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '8px',
                    marginBottom: '4px',
                    backgroundColor: selectedRegion?.id === region.id ? '#e6f7ff' : 'transparent',
                    cursor: 'pointer',
                    borderRadius: '4px',
                  }}
                  onClick={() => handleSelectRegion(region)}
                >
                  <span>{region.name}</span>
                  {region.is_custom && (
                    <Button
                      type="text"
                      size="small"
                      icon={<CloseOutlined />}
                      onClick={e => {
                        e.stopPropagation();
                        handleRemoveGroup(region.id);
                      }}
                    />
                  )}
                </div>
              ))}
            </div>
            <div style={{ marginTop: '16px' }}>
              <Space>
                <input
                  type="text"
                  placeholder="Enter group name"
                  value={newGroupName}
                  onChange={e => setNewGroupName(e.target.value)}
                  style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid #d9d9d9' }}
                />
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleAddGroup}
                  loading={loading}
                >
                  Add Group
                </Button>
              </Space>
            </div>
          </div>

          {/* Right: Countries in Selected Region */}
          <div style={{ flex: 1 }}>
            <div style={{ marginBottom: '16px', fontWeight: 'bold' }}>
              Countries {selectedRegion ? `in ${selectedRegion.slug}` : ''}
            </div>
            <div
              style={{
                border: '1px solid #d9d9d9',
                borderRadius: '4px',
                padding: '12px',
                minHeight: '200px',
              }}
            >
              {selectedRegion?.countries && selectedRegion.countries.length > 0 ? (
                selectedRegion.countries.map(country => (
                  <div
                    key={country.id}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '8px',
                      marginBottom: '4px',
                    }}
                  >
                    <span>
                      {country.name} ({country.iso_code})
                    </span>
                    <Button
                      type="text"
                      size="small"
                      icon={<CloseOutlined />}
                      onClick={() => handleRemoveCountry(country.id)}
                    />
                  </div>
                ))
              ) : (
                <div style={{ color: '#999', fontStyle: 'italic' }}>No countries selected</div>
              )}
            </div>
            {selectedRegion && (
              <div style={{ marginTop: '16px' }}>
                <Select
                  mode="multiple"
                  style={{ width: '100%', marginBottom: '8px' }}
                  placeholder="List of Countries"
                  value={selectedCountryIds}
                  onChange={setSelectedCountryIds}
                  showSearch
                  filterOption={(input, option) =>
                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  options={countries
                    .filter(country => !selectedRegion.countries?.some(rc => rc.id === country.id))
                    .map(country => ({
                      label: `${country.name},${country.iso_code}`,
                      value: country.id,
                    }))}
                />
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleAddCountry}
                  loading={loading}
                  disabled={selectedCountryIds.length === 0}
                >
                  Add Country
                </Button>
              </div>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
};

export default RegionsSection;
